import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Chip,
} from '@mui/material';
import { MdSystemUpdate, MdNewReleases, MdAutorenew } from 'react-icons/md';

const UpdatePrompt = () => {
    const [open, setOpen] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [waitingWorker, setWaitingWorker] = useState(null);

    const handleUpdateAvailable = useCallback((event) => {
        const { registration } = event.detail || {};
        if (registration && registration.waiting) {
            setWaitingWorker(registration.waiting);
            setOpen(true);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('pwaUpdateAvailable', handleUpdateAvailable);
        return () => {
            window.removeEventListener('pwaUpdateAvailable', handleUpdateAvailable);
        };
    }, [handleUpdateAvailable]);

    const handleUpdate = () => {
        if (!waitingWorker) return;

        setUpdating(true);

        // Tell the waiting service worker to skip waiting and become active
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });

        // Once the new SW takes control, reload to get fresh assets
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        });

        // Fallback: reload after 3 seconds in case controllerchange doesn't fire
        setTimeout(() => {
            window.location.reload();
        }, 3000);
    };

    const handleDismiss = () => {
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={updating ? undefined : handleDismiss}
            aria-labelledby="pwa-update-dialog-title"
            maxWidth="xs"
            fullWidth
            disableEscapeKeyDown={updating}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
                },
            }}
        >
            {/* Gradient header banner */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 60%, #3b82f6 100%)',
                    px: 3,
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(4px)',
                    }}
                >
                    <MdNewReleases size={26} color="#fff" />
                </Box>
                <Box>
                    <Typography
                        id="pwa-update-dialog-title"
                        variant="h6"
                        sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}
                    >
                        Update Available
                    </Typography>
                    <Chip
                        label="New Version Ready"
                        size="small"
                        sx={{
                            mt: 0.5,
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                            fontSize: '0.7rem',
                            height: 20,
                            backdropFilter: 'blur(4px)',
                        }}
                    />
                </Box>
            </Box>

            <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                    <Box
                        sx={{
                            backgroundColor: '#eff6ff',
                            borderRadius: '10px',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            mt: 0.25,
                        }}
                    >
                        <MdSystemUpdate size={22} color="#1d4ed8" />
                    </Box>
                    <Box>
                        <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}
                        >
                            A new version of Burray HR is ready
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                            We've rolled out improvements and new features. Refresh now to get the
                            latest experience — it only takes a second.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 3,
                    pt: 0,
                    gap: 1,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    onClick={handleDismiss}
                    disabled={updating}
                    color="inherit"
                    sx={{
                        color: '#64748b',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': { backgroundColor: '#f1f5f9' },
                    }}
                >
                    Later
                </Button>
                <Button
                    onClick={handleUpdate}
                    disabled={updating}
                    variant="contained"
                    startIcon={
                        updating ? (
                            <CircularProgress size={16} sx={{ color: '#fff' }} />
                        ) : (
                            <MdAutorenew size={18} />
                        )
                    }
                    sx={{
                        backgroundColor: '#1e3a8a',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': {
                            backgroundColor: '#1d4ed8',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 20px rgba(29,78,216,0.4)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {updating ? 'Updating…' : 'Update Now'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdatePrompt;
